<template>
	<section class="c-delivery-cart">
		<div class="c-delivery-cart__frame">
			<section class="c-delivery-cart__header">
				<div>
					<DsText htmlElement="h2" text-type="heading-2xl" text-style="bold">
						{{ labels.heading }}
					</DsText>
					<DsText
						v-if="!hideProductCount"
						html-element="span"
						text-type="body-lg">
						{{ numberOfSampleItems + numberOfItems }}
						{{
							numberOfSampleItems + numberOfItems === 1
								? labels.singleItemLabel
								: labels.pluralItemLabel
						}}
					</DsText>
				</div>
				<div>
					<DsButton
						variant="link"
						@keydown.enter="goToCart"
						@click="goToCart"
						tabindex="0"
					>
						{{ labels.editBasketLabel }}
					</DsButton>
				</div>
			</section>
			<div
				class="c-accordion c-accordion--basket"
				:class="{ 'is-active': isOpen === true }"
				v-if="showSampleMessage && !showLineItemSpecificMessage"
			>
				<button
					class="c-accordion__button"
					@click="openAccordion($event)"
					data-testid="accordion-button"
				>
					<h4>
						<DsIcon
							icon-name="information-circle"
							size="sm"
							style="padding-right: 6px;" />
						<DsText
							html-element="span"
							text-type="label-sm"
							text-style="medium">
							{{ labels.qualificationCallTitle }}
						</DsText>
					</h4>
					<DsIcon class="c-accordion__arrow-icon" icon-name="chevron-down" size="sm" />
				</button>
				<DsText
					html-element="div"
					text-type="sm">
					<div class="c-accordion__text" v-html="labels.qualificationCallText" />
				</DsText>
			</div>

			<notification
				:message="basket.informationMessages?.lineItemSpecificInformationMessage.message"
				:iconName="basket.informationMessages?.lineItemSpecificInformationMessage.iconName"
				v-if="showLineItemSpecificMessage" />

			<ul class="c-delivery-cart__line-items">
				<div
					class="c-global-basket__line-item"
					v-for="item in outOfPocketLineItems" :key="item.code">
					<OutOfPocketLineItems
						:size="'small'"
						:key="item.code"
						:item="item"
						:labels="labels"
						:marketSpecificTax="basket.marketSpecificTax"
						:is-summary="true"
						:showPricesForReimbursableItems="basket.showPricesForReimbursable"
					></OutOfPocketLineItems>
				</div>
				<div
					class="c-global-basket__line-item"
					v-for="item in samplesOutOfPocketLineItems" :key="item.code">
					<OutOfPocketLineItems
						:size="'small'"
						:key="item.code"
						:item="item"
						:labels="labels"
						:is-sample="true"
						:is-summary="true"
					></OutOfPocketLineItems>
				</div>
			</ul>
		</div>

		<ComplimentaryCart
			:labels="labels"
			:products="complimentaryLineItems"
			:hideComplimentaryProductQuantity="isComplimentaryItemsQuantityHidden"
		/>
	</section>
</template>
<script>
import Events from 'shared/settings/events';
import notification from 'shared/components/basket/notification.vue';
import OutOfPocketLineItems from 'shared/components/basket/basketLineItem.vue';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import ComplimentaryCart from './complimentaryCart.vue';
import { DsButton, DsText, DsIcon } from '@coloplast/design-system';

export default {
	props: {
		labels: {
			type: Object,
			required: true,
		},
		hideProductCount: {
			type: Boolean,
			default: false,
		}
	},
	data() {
		return {
			isOpen: false,
		};
	},
	components: {
		DsButton,
		DsText,
		ComplimentaryCart,
		OutOfPocketLineItems,
		DsIcon,
		notification
	},
	computed: {
		...mapGetters({
			culture: 'getCulture',
			basket: 'getBasket',
		}),
		numberOfItems() {
			return this.outOfPocketLineItems.length;
		},
		numberOfSampleItems() {
			return this.samplesOutOfPocketLineItems.length;
		},
		outOfPocketLineItems() {
			const allItems = this.basket?.paid || [];
			return allItems.filter((item) => !item.isComplimentary);
		},
		complimentaryLineItems() {
			const allItems = this.basket?.paid || [];
			return allItems.filter((item) => item.isComplimentary);
		},
		samplesOutOfPocketLineItems() {
			return this.basket && this.basket.samples ? this.basket.samples : [];
		},
		showSampleMessage() {
			return this.numberOfSampleItems !== 0 && this.labels.qualificationCallTitle
		},
		showLineItemSpecificMessage() {
			return this.basket.informationMessages?.lineItemSpecificInformationMessage?.showMessage || false;
		},
		isComplimentaryItemsQuantityHidden() {
			return this.basket.hideComplimentaryItemsQuantity;
		}
	},
	methods: {
		...mapActions({
			fetchBasket: 'fetchBasket',
			add: 'addItemToBasket',
		}),
		...mapMutations({
			setBasketApiCulture: 'setBasketApiCulture',
			setBasket: 'setBasket',
		}),
		goToCart(e) {
			e.preventDefault();
			const event = new CustomEvent(Events.ACTIVATE_FLY_OUT_BASKET, {});
			document.dispatchEvent(event);
		},
		openAccordion(event) {
			if (this.showLineItemSpecificMessage) return false;

			this.isOpen = !this.isOpen;
			const target = event.target;
			const parent = target.parentNode;
			const text = parent.querySelector('.c-accordion__text');
			if (this.isOpen === true && text != null) {
				parent.style.maxHeight = `${target.offsetHeight + text.offsetHeight + 10}px`;
			} else {
				parent.style.maxHeight = `${target.offsetHeight}px`;
			}
		},
	},
	async mounted() {
		this.setBasketApiCulture(this.culture);
		try {
			await this.fetchBasket();
		} catch (err) {
			console.error(err);
		}
	},
};
</script>
<style lang="scss">
@import 'multisite/styles/settings/settings';
@import 'multisite/styles/tools/tools';

.c-delivery-cart {
	&__frame {
		padding: 24px 20px 0;
		border-radius: 8px;
		box-shadow: ds-shadow('default');
		border: solid 1px rgba(0, 0, 0, 0.04);
		background-color: ds-color('bg', 'default');
	}

	&__header {
		display: flex;
		justify-content: space-between;
		align-items: baseline;
	}

	&__items {
		display: block;
	}

	&__line-items {
		margin: 24px 0 0;
		padding: 0;
		list-style: none;
	}

	.c-delivery-cart__line-items > li:first-child {
		padding-top: 0;
	}

	.c-delivery-cart__line-items > li:last-child {
		box-shadow: none;
	}

	.c-global-basket__line-item-heading {
		font-family: ds-font-family("heading");
		font-size: t-rem(16px);
		font-weight: ds-font-weight("bold");
		line-height: t-rem(20px);
		padding-right: 0;
	}

	.c-global-basket__item-details {
		line-height: 20px;

		span {
			color: ds-color('fg', 'default');
		}
	}

	span.c-global-basket__item-quantity-label {
		text-transform: capitalize;
		font-family: ds-font-family("body");
		font-size: t-rem(14px);
		font-weight: ds-font-weight("regular");
		line-height: t-rem(20px);
	}

	span.c-global-basket__item-total,
	span.c-global-basket__item-quantity {
		font-family: ds-font-family("body");
		font-size: t-rem(18px);
		line-height: t-rem(28px);
	}
}
</style>
